import React, {useState, useRef} from "react"
import {Button, Form, Input, notification, Modal, Radio, Empty} from "antd"
import {UserRound, MapPin, Plus, Building2, Home} from "lucide-react"
import COUNTRY from "country-json/src/country-by-abbreviation.json"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"

import {AccountAPI, buildRegistrationFromSubmit, buildInitialValuesFromProfile} from "./api"
import AccountEditorRender from "./AccountEditorRender"
import {requireEtatCountries} from "../WebEditor/formPlugins/index"

// Pays (code 2 lettres) → nom complet, et inverse — pour la logique conditionnelle
// code postal / état (alignée sur le module identity + le tunnel de paiement).
function countryCodeOf(paysName) {
    return COUNTRY.find(i => i.country === paysName)?.abbreviation || null
}

// Carte-panneau cohérente avec le dashboard (titre + icône d'accent).
function Panel({icon, accent, title, action, children}) {
    return (
        <div className="acs-panel" style={{background: "#fff", border: "1px solid #f0ece3", borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.04)"}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 16}}>
                <div style={{display: "flex", alignItems: "center", gap: 10}}>
                    <div style={{width: 36, height: 36, borderRadius: 10, background: accent + "1A", color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center"}}>{icon}</div>
                    <div style={{fontWeight: 700, fontSize: 16}}>{title}</div>
                </div>
                {action}
            </div>
            {children}
        </div>
    )
}

// Édition du MIROIR (account.profile). Sync append-only côté serveur.
//
// Le panneau « Mes informations » réutilise le MÊME module identity que le
// formulaire d'inscription (via AccountEditorRender + identityForm servi par /me)
// → cohérence parfaite des données saisies (mêmes champs, mêmes validations,
// même PhoneInput). Le bouton « Enregistrer » est porté hors du <Form> interne et
// déclenche la soumission via une ref.
export default function Profile({account, identityForm, org, lang, primary = "#2B70A9", onUpdated}) {
    const [saving, setSaving] = useState(false)
    const [addrModal, setAddrModal] = useState(false)
    const editorRef = useRef(null)
    const profile = account?.profile || {}
    const adresses = Array.isArray(profile.adresses) ? profile.adresses : []

    // Valeurs initiales : identité (name=["identity",...]) + champs custom du bloc
    // (clés racine "[TYPE]_…" converties au bon format par type). Permet d'éditer
    // l'identité ET les extras, exactement comme à l'inscription.
    const identityInitialValues = buildInitialValuesFromProfile(profile)

    // Soumission du form → remappe identité + champs custom vers le miroir et persiste.
    async function onIdentitySubmit(data) {
        const reg = buildRegistrationFromSubmit(data)
        setSaving(true)
        try {
            const payload = {
                nom: reg.nom, prenom: reg.prenom, email: reg.email, tel: reg.tel, homme: reg.homme,
            }
            // extras (champs custom du bloc) → stockés dans profile.extras et, si le
            // compte est VALIDE, posés sur le contact (assignExtrasToContact). On ne
            // transmet `extras` QUE si le form porte effectivement un bloc (sinon on
            // n'écrase pas d'éventuels extras existants avec un objet vide).
            if (reg.extras !== undefined) {
                payload.extras = reg.extras
            }
            const res = await AccountAPI.editProfile(payload)
            if (res?.success) {
                notification.success({message: t`Profil mis à jour`})
                if (onUpdated && res.account) onUpdated(res.account)
            } else {
                notification.error({message: t`Erreur lors de la mise à jour`})
            }
        } finally {
            setSaving(false)
        }
    }

    async function onAddAddress(values) {
        const isPro = values.type === "PRO"
        const res = await AccountAPI.addAddress({
            type: isPro ? "PRO" : "PERSO",
            // En mode Entreprise, le libellé de l'adresse = la raison sociale.
            nom: isPro ? (values.raison || null) : (values.nom || null),
            raison: isPro ? (values.raison || null) : null,
            // Identifiant société générique (SIREN, n° TVA, Company ID…) — OPTIONNEL.
            siren: isPro ? (values.siren || null) : null,
            adresse: values.adresse,
            postal: values.postal || null,
            ville: values.ville,
            etat: values.etat || null,
            pays: values.pays || "France",
        })
        if (res?.success) {
            notification.success({message: t`Adresse ajoutée`})
            setAddrModal(false)
            if (onUpdated && res.account) onUpdated(res.account)
        } else {
            notification.error({message: t`Erreur`})
        }
    }

    return (
        <div>
            <Panel icon={<UserRound size={20}/>} accent={primary} title={t`Mes informations`}>
                {identityForm ? (
                    <>
                        <AccountEditorRender
                            ref={editorRef}
                            form={identityForm}
                            org={org}
                            lang={lang}
                            initialValues={identityInitialValues}
                            onData={onIdentitySubmit}
                        />
                        <Button
                            type="primary"
                            loading={saving}
                            onClick={() => editorRef.current && editorRef.current.submit()}
                            style={{borderRadius: 10, fontWeight: 600}}
                        >
                            <Trans>Enregistrer</Trans>
                        </Button>
                    </>
                ) : (
                    <Empty description={t`Chargement…`} style={{padding: "16px 0"}}/>
                )}
            </Panel>

            <Panel
                icon={<MapPin size={20}/>} accent="#1E9E6A" title={t`Mes adresses`}
                action={<Button icon={<Plus size={16}/>} onClick={() => setAddrModal(true)} style={{borderRadius: 10}}><Trans>Ajouter</Trans></Button>}
            >
                {adresses.length === 0 ? (
                    <Empty description={t`Aucune adresse enregistrée`} style={{padding: "16px 0"}}/>
                ) : (
                    adresses.map((a, i) => {
                        const pro = a.type === "PRO"
                        return (
                            <div key={a.id || i} style={{
                                display: "flex", alignItems: "center", gap: 12,
                                padding: "12px 14px", marginBottom: 8,
                                background: "#faf8f4", border: "1px solid #f0ece3", borderRadius: 12,
                            }}>
                                <div style={{flexShrink: 0, width: 36, height: 36, borderRadius: 9, background: (pro ? "#7C5CD6" : "#1E9E6A") + "14", color: pro ? "#7C5CD6" : "#1E9E6A", display: "inline-flex", alignItems: "center", justifyContent: "center"}}>
                                    {pro ? <Building2 size={18}/> : <Home size={18}/>}
                                </div>
                                <div style={{minWidth: 0, flex: 1}}>
                                    {a.nom && <div style={{fontWeight: 600, fontSize: 14}}>{a.nom}</div>}
                                    <div style={{fontSize: 13, color: "#8a8278"}}>{[a.adresse, a.postal, a.ville, a.etat, a.pays].filter(Boolean).join(", ")}</div>
                                    {pro && a.siren && <div style={{fontSize: 12, color: "#a59c8e", marginTop: 2}}>{t`ID société`} : {a.siren}</div>}
                                </div>
                                <span style={{flexShrink: 0, fontSize: 11.5, fontWeight: 600, color: pro ? "#7C5CD6" : "#1E9E6A", background: (pro ? "#7C5CD6" : "#1E9E6A") + "14", borderRadius: 999, padding: "3px 10px"}}>
                                    {pro ? t`Entreprise` : t`Particulier`}
                                </span>
                            </div>
                        )
                    })
                )}
            </Panel>

            <Modal title={t`Nouvelle adresse`} open={addrModal} onCancel={() => setAddrModal(false)} footer={null} destroyOnHidden>
                <Form layout="vertical" onFinish={onAddAddress} initialValues={{type: "PERSO", pays: "France"}}>
                    {/* Type : Particulier / Entreprise — aligné sur le tunnel de paiement. */}
                    <Form.Item name="type" label={t`Type d'adresse`}>
                        <Radio.Group optionType="button" buttonStyle="solid">
                            <Radio.Button value="PERSO"><Trans>Particulier</Trans></Radio.Button>
                            <Radio.Button value="PRO"><Trans>Entreprise</Trans></Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    {/* Bloc Entreprise (conditionnel) : raison sociale requise + identifiant
                        société OPTIONNEL et international (SIREN, n° TVA, Company ID…). */}
                    <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
                        {(_form) => {
                            const isPro = _form.getFieldValue("type") === "PRO"
                            if (isPro) {
                                return (
                                    <>
                                        <Form.Item name="raison" label={t`Raison sociale`} rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                            <Input placeholder={t`Nom de la société`} autoComplete="organization"/>
                                        </Form.Item>
                                        <Form.Item
                                            name="siren"
                                            label={t`Identifiant société (optionnel)`}
                                            help={t`SIREN, n° de TVA, Company ID… selon votre pays`}
                                        >
                                            <Input placeholder={t`SIREN / n° TVA / Company ID`}/>
                                        </Form.Item>
                                    </>
                                )
                            }
                            // Particulier : libellé libre optionnel (Domicile, Bureau…).
                            return (
                                <Form.Item name="nom" label={t`Libellé (optionnel)`}>
                                    <Input placeholder={t`Domicile, Bureau…`}/>
                                </Form.Item>
                            )
                        }}
                    </Form.Item>

                    <Form.Item name="adresse" label={t`Adresse`} rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                        <Input autoComplete="address-line1"/>
                    </Form.Item>

                    {/* Code postal masqué pour Israël (comme le module identity / tunnel). */}
                    <div style={{display: "flex", gap: 12, flexWrap: "wrap"}}>
                        <Form.Item noStyle shouldUpdate={(p, c) => p.pays !== c.pays}>
                            {(_form) => {
                                const code = countryCodeOf(_form.getFieldValue("pays"))
                                if (code === "IL") return null
                                return (
                                    <Form.Item name="postal" label={t`Code postal`} rules={[{required: true, message: t`Ce champ est obligatoire`}]} style={{flex: 1, minWidth: 120}}>
                                        <Input autoComplete="postal-code"/>
                                    </Form.Item>
                                )
                            }}
                        </Form.Item>
                        <Form.Item name="ville" label={t`Ville`} rules={[{required: true, message: t`Ce champ est obligatoire`}]} style={{flex: 2, minWidth: 160}}>
                            <Input autoComplete="address-level2"/>
                        </Form.Item>
                    </div>

                    {/* État / Province : conditionnel selon le pays (US/CA/…). */}
                    <Form.Item noStyle shouldUpdate={(p, c) => p.pays !== c.pays}>
                        {(_form) => {
                            const code = countryCodeOf(_form.getFieldValue("pays"))
                            if (!requireEtatCountries.includes(code)) return null
                            return (
                                <Form.Item name="etat" label={t`État / Province`} rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                                    <Input autoComplete="address-level1"/>
                                </Form.Item>
                            )
                        }}
                    </Form.Item>

                    {/* Pays : <select> country-json (même rendu que le tunnel / identity). */}
                    <Form.Item name="pays" label={t`Pays`} rules={[{required: true, message: t`Ce champ est obligatoire`}]}>
                        <select className="form-control">
                            <option disabled value="">{t`Pays`}</option>
                            {COUNTRY.map(i => <option key={i.abbreviation} value={i.country}>{i.country}</option>)}
                        </select>
                    </Form.Item>

                    <Button type="primary" htmlType="submit" block style={{borderRadius: 10, fontWeight: 600}}>
                        <Trans>Ajouter l'adresse</Trans>
                    </Button>
                </Form>
            </Modal>
        </div>
    )
}
